import { useState } from 'react'

import { GetPlayerWithDepositPromotionData } from '@/modules/manage-player/type'
import { columns } from '../PlayerWithDepositPromotionTable/constants'
import { DataTableArrow } from '@/common/ui/table/data-table-arrow'
import { Card } from '@/common/ui/card'
import { Separator } from '@/common/ui/separator'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { Button } from '@/common/ui/button'
import { ArrowLeft, Download } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useMutationExportPlayersWithDepositPromotionCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayersWithDepositPromotionCsv'
import type { GetPlayerWithDepositPromotionRequest } from '@/modules/manage-player/type'
import PlayerSearchControls from '@/common/components/PlayerSearchControls'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'

interface PlayerWithDepositPromotionListingProps {
	data: GetPlayerWithDepositPromotionData[]
	total: number
	isLoading?: boolean
	showBackButton?: boolean
	exportParams?: Pick<
		GetPlayerWithDepositPromotionRequest,
		'phone' | 'startDate' | 'endDate'
	>
}

export default function PlayerWithDepositPromotionListing({
	data,
	total,
	isLoading,
	showBackButton = true,
	exportParams,
}: PlayerWithDepositPromotionListingProps) {
	const navigate = useNavigate()
	const exportCsvMutation = useMutationExportPlayersWithDepositPromotionCsv()
	const [showExportModal, setShowExportModal] = useState(false)

	const handleExportCsv = (amount: number) => {
		if (!exportParams) return

		exportCsvMutation.mutate({
			...exportParams,
			take: amount || total || 1000,
			skip: 0,
			filename: 'players_with_deposit_promotion',
			columnMappings: {
				phone: 'เบอร์โทรศัพท์',
				totalPromotionalDepositAmount: 'ยอดฝากโปรโมชั่นทั้งหมด (บาท)',
				totalBonusAmount: 'ยอดโบนัสทั้งหมด (บาท)',
				createdAt: 'วันที่สมัคร',
				'lastLogin.ipAddress': 'IP Address',
			},
		} as any)
	}

	return (
		<Card>
			<div className="space-y-4 p-3 sm:p-4">
				{showBackButton && (
					<Button
						onClick={() => navigate(-1)}
						variant="outline"
						size="sm"
						className="mb-4 flex items-center gap-2"
					>
						<ArrowLeft className="h-4 w-4" />
						ย้อนกลับ
					</Button>
				)}

				{/* Header Section - Mobile Responsive */}
				<div className="space-y-4">
					{/* Title and Date Controls Row */}
					<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
						{/* Title */}
						<div>
							<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
								ผู้เล่นที่มียอดฝากโปรโมชั่น
							</h2>
							<p className="text-sm text-muted-foreground sm:text-base">
								รายการผู้เล่นที่มียอดฝากโปรโมชั่นทั้งหมด {total}
							</p>
						</div>

						{/* Date Controls and Export */}
						<div className="flex flex-col gap-3 sm:items-end">
							<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setShowExportModal(true)}
									disabled={exportCsvMutation.isPending || !exportParams}
									className="flex items-center gap-2"
								>
									<Download className="h-4 w-4" />
									<span className="sm:inline">โหลด CSV</span>
								</Button>
								<DateRangePicker />
							</div>
							<DateRangeButtons />
						</div>
					</div>

					{/* Search Controls */}
					<PlayerSearchControls searchHint="สามารถค้นหาด้วย: เบอร์โทร, IP Address" />
				</div>

				<Separator className="my-4 sm:my-6" />

				{/* Table Section */}
				<div className="space-y-4">
					<DataTableArrow
						columns={columns}
						data={data}
						total={total}
						isLoading={isLoading}
					/>
				</div>
			</div>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</Card>
	)
}
